/**
 * @fileoverview API Error classes for the unified API layer
 * Provides structured error handling with proper typing
 */

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly url?: string;
  public readonly details?: unknown;
  public readonly type = 'api_error';

  constructor(
    message: string,
    statusCode: number = 0,
    code: string = 'UNKNOWN_ERROR',
    url?: string,
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.url = url;
    this.details = details;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  // Helper methods for error classification
  isRetryable(): boolean {
    // Retry on 5xx server errors or network errors
    return this.statusCode >= 500 || this.statusCode === 0;
  }

  isAuthError(): boolean {
    return this.statusCode === 401 || this.statusCode === 403;
  }

  isClientError(): boolean {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  isServerError(): boolean {
    return this.statusCode >= 500;
  }

  getSeverity(): 'low' | 'medium' | 'high' | 'critical' {
    if (this.statusCode >= 500) return 'critical';
    if (this.statusCode === 429) return 'high';
    if (this.statusCode >= 400) return 'medium';
    return 'low';
  }

  toJSON() {
    return {
      type: this.type,
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      code: this.code,
      url: this.url,
      details: this.details,
      stack: this.stack
    };
  }
}

// Helper functions for creating errors from different sources
export function mapHttpStatusToErrorCode(status: number): string {
  switch (status) {
    case 400: return 'BAD_REQUEST';
    case 401: return 'UNAUTHORIZED';
    case 403: return 'FORBIDDEN';
    case 404: return 'NOT_FOUND';
    case 409: return 'CONFLICT';
    case 422: return 'VALIDATION_ERROR';
    case 429: return 'RATE_LIMITED';
    case 500: return 'INTERNAL_SERVER_ERROR';
    case 502: return 'BAD_GATEWAY';
    case 503: return 'SERVICE_UNAVAILABLE';
    case 504: return 'GATEWAY_TIMEOUT';
    default: return 'UNKNOWN_ERROR';
  }
}

export function createErrorFromResponse(response: Response, requestUrl?: string): Promise<ApiError> {
  return response.json()
    .then((data: unknown) => {
      const errorData = data as Record<string, unknown>;

      const nestedError = errorData.error;
      const nestedErrorMessage = (
        typeof nestedError === 'object' &&
        nestedError !== null &&
        'message' in nestedError &&
        typeof (nestedError as Record<string, unknown>).message === 'string'
      )
        ? ((nestedError as Record<string, unknown>).message as string)
        : undefined;

      const errorMessage =
        (typeof errorData.message === 'string' ? errorData.message : undefined) ||
        (typeof errorData.error === 'string' ? errorData.error : undefined) ||
        nestedErrorMessage ||
        response.statusText ||
        'Request failed';

      return new ApiError(
        errorMessage,
        response.status,
        (errorData.code as string) || mapHttpStatusToErrorCode(response.status),
        requestUrl,
        errorData.details
      );
    })
    .catch(() => {
      // If response is not JSON, create error with status info
      return new ApiError(
        response.statusText || 'Request failed',
        response.status,
        mapHttpStatusToErrorCode(response.status),
        requestUrl
      );
    });
}

export function createErrorFromNetworkError(error: unknown, url?: string): ApiError {
  if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
    return new ApiError('Request timeout', 0, 'TIMEOUT', url);
  }

  const errorMessage = error instanceof Error ? error.message : String(error);

  if (errorMessage.includes('fetch')) {
    return new ApiError('Network error occurred', 0, 'NETWORK_ERROR', url);
  }

  if (errorMessage.includes('ECONNREFUSED')) {
    return new ApiError('Connection refused', 0, 'CONNECTION_REFUSED', url);
  }

  return new ApiError(
    errorMessage || 'Unknown network error',
    0,
    'NETWORK_ERROR',
    url
  );
}
